import { create } from 'zustand';
import { api } from '../api/client';

export type Role = 'admin' | 'editor' | 'viewer';

export interface Me {
  id: number;
  username: string;
  role: Role;
  displayName: string | null;
  mustChangePassword?: boolean;
  imagePath?: string | null;
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  loaded: boolean;
  fetchMe: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  me: null,
  loading: false,
  loaded: false,
  async fetchMe() {
    set({ loading: true });
    try {
      const me = await api.get<Me>('/auth/me');
      set({ me, loading: false, loaded: true });
    } catch {
      set({ me: null, loading: false, loaded: true });
    }
  },
  async login(username, password) {
    const me = await api.post<Me>('/auth/login', { username, password });
    set({ me, loaded: true });
  },
  async logout() {
    await api.post('/auth/logout');
    set({ me: null });
  },
  async changePassword(currentPassword, newPassword) {
    await api.post('/auth/change-password', { currentPassword, newPassword });
    set((s) => (s.me ? { me: { ...s.me, mustChangePassword: false } } : s));
  },
}));

// VIEWER and EDITOR share the same access level for now — canWrite is true for all
// authenticated non-admin operations; admin-only UI remains gated via isAdmin().
export function canWrite(role: Role | undefined | null): boolean {
  return role === 'admin' || role === 'editor' || role === 'viewer';
}
export function isAdmin(role: Role | undefined | null): boolean {
  return role === 'admin';
}
